import React, { useEffect, useState } from 'react';
import { CarouselDisplayStyle, } from '@honeycomb/data';
import { Styler, LazyLoadComponent, useDisclosure, Carousel, CarouselViewport, CarouselSlide, CarouselDotButtons, } from '@honeycomb/ui-core';
import { pushPromotedDeals } from '@honeycomb/tracking';
import dynamic from 'next/dynamic';
import { useGenericRootComponent } from '../GenericRootComponent/GenericRootComponent.context';
import { DealCard } from '../DealCard/DealCard';
import { PriceBlockCard } from '../PriceBlockCard/PriceBlockCard';
import { MerchandiseCard } from '../MerchandiseCard/MerchandiseCard';
import { CAROUSEL_THEME } from '../../../types/carousel-types';
import { HotelReviewsDialog } from '../../hotel';
const DestinationCardDialog = dynamic(() => import('../DestinationCardDialog').then((module) => module.DestinationCardDialog), { ssr: false });
const HotelProductCard = dynamic(() => import('../HotelProductCard').then((module) => module.HotelProductCard), { ssr: false });
const TabbedDealCard = dynamic(() => import('../TabbedDealCard').then((module) => module.TabbedDealCard), { ssr: false });
const DestinationCard = dynamic(() => import('../DestinationCard').then((module) => module.DestinationCard), { ssr: false });
const GenericCard = dynamic(() => import('../GenericCard').then((module) => module.GenericCard), {
    ssr: false,
});
export function GenericCardCarousel(props) {
    const { carousel, tabIndex, moreLinkText, zoneIds } = props;
    const { slides } = carousel;
    // Hotel reviews dialog state
    const [reviewsDialogOpen, { open: handleOpenReviewsDialog, close: handleCloseReviewsDialog }] = useDisclosure(false);
    const [reviewsDialogExited, setReviewsDialogExited] = useState(false);
    const [reviewDetails, setReviewDetails] = useState(null);
    const handleShowReviewsDialog = React.useCallback((hotel) => (origin) => {
        setReviewDetails({ hotel, origin });
        handleOpenReviewsDialog();
        setReviewsDialogExited(false);
    }, [handleOpenReviewsDialog]);
    // Destination card dialog state
    const [destinationDialogOpen, { open: handleOpenDestinationDialog, close: handleCloseDestinationDialog }] = useDisclosure(false);
    const [destinationDialogExited, setDestinationDialogExited] = useState(false);
    const [selectedDestinationCard, setSelectedDestinationCard] = useState(null);
    const handleShowDestinationDialog = React.useCallback((card) => {
        setSelectedDestinationCard(card);
        handleOpenDestinationDialog();
        setDestinationDialogExited(false);
    }, [handleOpenDestinationDialog]);
    const { carouselDisplayStyle, backgroundColor } = useGenericRootComponent();
    const showCardBorders = backgroundColor === 'common.white';
    const isFullWidth = carouselDisplayStyle === CarouselDisplayStyle.FullWidth;
    useEffect(() => {
        const dealCards = slides.filter((slide) => slide.__typename === 'DealCard');
        if (dealCards.length > 0)
            pushPromotedDeals(dealCards);
    }, [slides]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Styler, { pos: "relative" },
            React.createElement(Carousel, { visibleSlides: CAROUSEL_THEME.visibleItems[carouselDisplayStyle], slideSpacing: CAROUSEL_THEME.spacing[carouselDisplayStyle], overflow: isFullWidth ? 'hidden' : 'visible', title: "Card carousel" },
                React.createElement(CarouselViewport, { ArrowButtonProps: { size: 'l', withBorder: true } }, slides.map((card, index) => {
                    const key = `slide-${index}`;
                    // Hotel card
                    if (card.__typename === 'HotelCard') {
                        return (React.createElement(CarouselSlide, { key: key },
                            React.createElement(HotelProductCard, { onReviewsDialogTriggerClick: handleShowReviewsDialog(card.hotel), card: card, withBorder: showCardBorders })));
                    }
                    // Destination card
                    if (card.__typename === 'DestinationCard') {
                        return (React.createElement(CarouselSlide, { key: key },
                            React.createElement(DestinationCard, { card: card, onShowMore: () => handleShowDestinationDialog(card), index: index.toString(), typename: card.__typename, tabIndex: tabIndex, withBorder: showCardBorders })));
                    }
                    // Deal card
                    if (card.__typename === 'DealCard') {
                        return (React.createElement(CarouselSlide, { key: key },
                            React.createElement(DealCard, { card: card, withBorder: showCardBorders })));
                    }
                    // TabbedDeal card
                    if (card.__typename === 'TabbedDealCard') {
                        return (React.createElement(CarouselSlide, { key: key },
                            React.createElement(TabbedDealCard, { card: card, withBorder: showCardBorders })));
                    }
                    // Price block card
                    if (card.__typename === 'PriceBlockCards') {
                        return (React.createElement(CarouselSlide, { key: key },
                            React.createElement(PriceBlockCard, { cards: card, withBorder: showCardBorders })));
                    }
                    // Merchandise card
                    if (card.__typename === 'MerchandiseCard') {
                        return (React.createElement(CarouselSlide, { key: key },
                            React.createElement(MerchandiseCard, { card: card, moreLinkText: moreLinkText, carouselDisplayStyle: carouselDisplayStyle, zoneIds: zoneIds })));
                    }
                    // Generic card
                    if (card.__typename === 'Card') {
                        return (React.createElement(CarouselSlide, { key: key },
                            React.createElement(GenericCard, { card: card, withBorder: showCardBorders })));
                    }
                    return null;
                })),
                isFullWidth && (React.createElement(Styler, { pos: "absolute", bottom: 1, left: 0, right: 0, m: "0 auto", w: "fit-content", zIndex: 1 },
                    React.createElement(CarouselDotButtons, { bg: "transparent" }))))),
        selectedDestinationCard && (destinationDialogOpen || !destinationDialogExited) && (React.createElement(LazyLoadComponent, { intersectionProps: { triggerOnce: true }, child: React.createElement(DestinationCardDialog, { card: selectedDestinationCard, open: destinationDialogOpen, onClose: handleCloseDestinationDialog, onExited: () => setDestinationDialogExited(true) }) })),
        reviewDetails && (reviewsDialogOpen || !reviewsDialogExited) && (React.createElement(HotelReviewsDialog, { hotel: reviewDetails.hotel, origin: reviewDetails.origin, open: reviewsDialogOpen, onClose: handleCloseReviewsDialog, onExited: () => setReviewsDialogExited(true) }))));
}
